import axios, { type AxiosRequestConfig } from "axios";
import type { LinkElementData } from "@/components/ui/LinkElement.vue";
import type { LinkData, ButtonData, RichText } from "../../types";

export interface FooterData {
   heading: string;
   copyright: string;
   // a footer will either have contact links or a contact button (which opens intercom)
   contactOptions: [LinkData | ButtonData];
   navLinks: { type: "link"; url: string; text: string }[];
   showPoweredBy: boolean;
   poweredByLinkEnabled: boolean;
}

export interface BaseContent {
   siteNav: any;
   footer: FooterData;
   logo: any;
   pageMeta: any;
   organisationInfo: any;
   cookieBanner: any;
   type: "site_base_layout";
}
export interface BaseResp {
   id: string;
   contentVersionId: string;
   internalName: "_base";
   key: "_base";
   content: BaseContent;
   studyId: string | null;
   localeId: string;
   createdAt: string;
   updatedAt: string;
   type: "page";
   stages: any[];
}

export interface SignupContent {
   communicationsPolicy: RichText;
   signupPolicy: RichText;
   registrationCode: {
      heading: string;
      content: RichText;
   } | null;
   type: "signup_page";
}
export interface SignUpResp {
   id: string;
   key: "signup";
   localeId: string;
   contentVersionId: string;
   internalName: "signup";
   studyId: string | null;
   updatedAt: string;
   createdAt: string;
   content: SignupContent;
   type: "page";
   stages: any[];
}

export interface PageContent {
   type: "default_page";
   heading: string;
   sections: any[]; // TODO: replace all with section types
}
export interface PageResp {
   id: string;
   versionId: string;
   key: "landing" | "signup" | "privacy" | "home";
   content: PageContent;
   createdAt: string;
   updatedAt: string;
   localeId: string;
}

interface FlowselNavSupportButton {
   text: "Support";
   action: "intercom";
   type: "button";
}
interface FlowselResp {
   id: string;
   versionId: string;
   key: "flowsel";
   content: {
      topnav: {
         support: FlowselNavSupportButton | LinkElementData;
      };
   };
   createdAt: string;
   updatedAt: string;
   localeId: string;
}

export default class ContentApi {
   public static getAccount = async (): Promise<PageResp> => {
      const response = await axios.get(`/content/account`);
      return response?.data?.data;
   };

   public static getBase = async (): Promise<BaseResp> => {
      const response = await axios.get(`/content/_base`);
      return response?.data?.data;
   };

   public static getLanding = async (): Promise<PageResp> => {
      const response = await axios.get(`/content/landing`);
      return response?.data?.data;
   };

   public static getHome = async (): Promise<PageResp> => {
      const response = await axios.get(`/content/home`);
      return response?.data?.data;
   };

   public static getSignup = async (): Promise<SignUpResp> => {
      const response = await axios.get(`/content/signup`);
      return response?.data?.data;
   };

   public static getPrivacy = async (): Promise<PageResp> => {
      const response = await axios.get(`/content/privacy`);
      return response?.data?.data;
   };

   public static getTerms = async (): Promise<PageResp> => {
      const response = await axios.get(`/content/terms`);
      return response?.data?.data;
   };

   public static getFlowsel = async (): Promise<FlowselResp> => {
      const response = await axios.get(`/content/flowsel`);

      return response?.data?.data;
   };

   // bespoke (requires key)
   public static getBespokeContent = async (key: string): Promise<PageResp> => {
      const response = await axios.get(`/content/${key}`);
      return response?.data?.data;
   };
}
